/**
 * The mock data source behind `/test`. A port of the Reef prototype's
 * deterministic data layer (seeded corpus, fixed clock 2026-09-15) exposed
 * through the same contract the live API implements.
 *
 * Loaded only through `data-sources/load.ts`, and only in builds where
 * VITE_ENABLE_TEST_ROUTES is true — it is its own `mock-engine` chunk.
 */
import type { DataSource, WidgetId, WidgetLoaders } from '@/data-sources/contract';

import { mockBootstrap, mockCredits } from './bootstrap';
import { mockChatApi } from './chat';
import { chatDb } from './chat/db';
import { mockConversationsApi } from './conversations';
import { mockInfluencersApi } from './influencers';
import { mockQuickSearchApi } from './quick-search';
import { mockReportsApi } from './reports';
import { mockSettingsApi } from './settings';
import { assertNoXKeyCollision } from './engine/aggregate';
import { simulate, shouldBeEmpty } from './scenarios';
import { mockWidgetLoaders } from './widgets';

assertNoXKeyCollision();

/** Wrap every loader with latency, `?mock-error` and `?mock-empty`. */
function withScenarios(loaders: WidgetLoaders): WidgetLoaders {
  const wrapped = {} as Record<WidgetId, WidgetLoaders[WidgetId]>;
  for (const id of Object.keys(loaders) as WidgetId[]) {
    const load = loaders[id] as (req: unknown, ctx: unknown) => Promise<unknown>;
    wrapped[id] = (async (
      request: { query: { selections: object } },
      ctx: { signal?: AbortSignal },
    ) => {
      await simulate(id, ctx.signal);
      const req = shouldBeEmpty(id)
        ? {
            ...request,
            query: { ...request.query, selections: { ...request.query.selections, sentiment: [] } },
          }
        : request;
      return load(req, ctx);
    }) as WidgetLoaders[WidgetId];
  }
  return wrapped as WidgetLoaders;
}

export const mockSource: DataSource = {
  ...mockConversationsApi,
  // Sample data keeps no saved filters: every visit starts from the defaults.
  async getSavedFilters() {
    return null;
  },
  async saveFilters() {},
  ...mockReportsApi,
  ...mockChatApi,
  ...mockQuickSearchApi,
  ...mockSettingsApi,
  ...mockInfluencersApi,
  id: 'mock',
  async bootstrap(ctx) {
    // No latency: the prototype drew its chrome instantly; only data "settles".
    await simulate('bootstrap', ctx.signal, { latency: false });
    return mockBootstrap();
  },
  async credits(ctx) {
    await simulate('credits', ctx.signal);
    return mockCredits(chatDb.creditsSpent);
  },
  widgets: withScenarios(mockWidgetLoaders),
};
